import type { ApiClient, SetupState } from "./api";
import { apiProblem, useLoaded } from "./useLoaded";

export interface SetupStateHook {
  /** Null until the first answer; the last good answer after a read that failed. */
  setup: SetupState | null;
  problem: string | null;
  refresh: () => void;
}

/**
 * UI-13 — what still stops a recording running end to end, held once for the whole app.
 *
 * Held at the top rather than in the Setup page because two things read it and they must
 * never disagree: the page draws the rows, and the rail's settings button carries a
 * warning dot while anything is blocking. A dot that says something needs attention over
 * a page that says everything is fine is worse than no dot at all.
 *
 * Refreshed on a slow timer as well as on demand. Every row is derived from live state —
 * a worker's heartbeat, what a machine has on disk, whether a key is stored — so it goes
 * stale on its own, without anybody in this browser having done anything. A minute is
 * far longer than the recordings poll because none of these change while somebody
 * watches: a PC comes back, a download finishes, a key is entered on the very screen
 * that then asks for a refresh.
 */
const Interval = 60_000;

const readSetup = (api: ApiClient): Promise<SetupState | null> => api.getSetup();
const problem = apiProblem("The setup checklist could not be read.");

export function useSetup(api: ApiClient): SetupStateHook {
  const { value, problem: failure, refresh } = useLoaded(api, readSetup, null, problem, { poll: Interval });

  return { setup: value, problem: failure, refresh };
}
